import { KHPoint } from '../../../../../LearningWorlds/pixi-puzzle-framework/PuzzleEngine/KHPoint';
import { applyFrameToElement, MANIP_FRAME_ACTIVE, MANIP_FRAME_IDLE } from '../../../../../utils/utils';

import { ScorableDragRegion } from './ScorableDragRegion';

export type DropRegionConfig = {
  parent: HTMLElement;
  frame: string;
  key: string;
  hasoutline: boolean;
  issinglemode: boolean;
  ignoreinscore?: boolean;
  defaultvalue?: string;
  shape: string;
  backgroundcolor: string;
};

export class ScorableDropRegion {
  public config: DropRegionConfig;
  public dropRegion: HTMLDivElement;
  public occupiedDragRegions: ScorableDragRegion[] = [];
  public onChangeCallback: (() => void) | undefined;
  private hasOutline: boolean = false;

  public constructor(config: DropRegionConfig) {
    this.dropRegion = document.createElement('div');
    config.parent.appendChild(this.dropRegion);

    this.config = config;
    this.hasOutline = this.config.hasoutline;

    if (this.dropRegion) {
      applyFrameToElement(this.dropRegion, this.config.frame);
    }

    const isOval = this.config.shape?.toLocaleLowerCase() === 'oval';

    this.dropRegion.style.borderRadius = isOval ? '50%' : '';
    this.dropRegion.style.backgroundColor = this.config.backgroundcolor;
    this.dropRegion.style.border = this.hasOutline ? MANIP_FRAME_IDLE : 'none';

    // if it's not an oval but has an outline, we want to round the corners a bit
    if (!isOval && this.hasOutline) {
      this.dropRegion.style.borderRadius = '5px';
    }
  }

  public addOccupiedRegion(region: ScorableDragRegion): void {
    this.occupiedDragRegions.push(region);

    if (this.onChangeCallback) {
      this.onChangeCallback();
    }

    this.updateBorderStyle();
  }

  public removeOccupiedRegion(region: ScorableDragRegion): void {
    this.occupiedDragRegions = this.occupiedDragRegions.filter(r => r !== region);

    if (this.onChangeCallback) {
      this.onChangeCallback();
    }

    this.updateBorderStyle();
  }

  public getRect(): DOMRect {
    return this.dropRegion.getBoundingClientRect();
  }

  public getKeyAnswerStateData(): { dropRegionKey: string; answerValue: string, stateValue: string } {
    let valueString = '';
    let keyString = '';

    if (this.occupiedDragRegions.length === 0 && this.config.defaultvalue) {
      valueString = this.config.defaultvalue;
    }
    else if (this.occupiedDragRegions.length === 1) {
      valueString = this.occupiedDragRegions[0].getAnswerValue();
      keyString = this.occupiedDragRegions[0].config.key;
    } else {
      keyString = `[${this.occupiedDragRegions.map(r => r.config.key).join(',')}]`;
      valueString = `[${this.occupiedDragRegions.map(r => r.getAnswerValue()).join(',')}]`;
    }

    return {
      dropRegionKey: this.config.key,
      answerValue: valueString,
      stateValue: keyString
    };
  }

  public getManipulativePosition(): KHPoint {
    return {
      x: parseFloat(this.dropRegion.style.left),
      y: parseFloat(this.dropRegion.style.top)
    };
  }

  public getCenterPosition(): KHPoint {
    const halfWidth = this.dropRegion.clientWidth / 2;
    const halfHeight = this.dropRegion.clientHeight / 2;

    let borderSize = parseFloat(this.dropRegion.style.border);
    if (isNaN(borderSize)) {
      borderSize = 0;
    }
    
    return {
      x: parseFloat(this.dropRegion.style.left) + halfWidth + borderSize,
      y: parseFloat(this.dropRegion.style.top) + halfHeight + borderSize
    };
  }

  public getTopLeftPosition(): KHPoint {
    return {
      x: parseFloat(this.dropRegion.style.left),
      y: parseFloat(this.dropRegion.style.top)
    };
  }

  public getRandomPositionInside(): KHPoint {
    const rect = this.getRect();
    return {
      x: Math.random() * rect.width,
      y: Math.random() * rect.height
    };
  }

  public isValidDrop(): boolean {
    if (this.config.issinglemode && this.occupiedDragRegions.length >= 1) {
      return false;
    }

    return true;
  }

  public isSingle(): boolean {
    return this.config.issinglemode;
  }

  public setIsHoverTarget(isHovering: boolean): void {
    if (this.hasOutline) {
      this.dropRegion.style.border = isHovering ? MANIP_FRAME_ACTIVE : this.getBorderStyle();
    }
  }

  private getBorderStyle(): string {
    return this.occupiedDragRegions.length >= 1 ? MANIP_FRAME_ACTIVE : MANIP_FRAME_IDLE;
  }

  private updateBorderStyle(): void {
    if (this.hasOutline) {
      this.dropRegion.style.border = this.getBorderStyle();
    }
  }
}
